/**
 * §10.4 follow-on — period history & pack download.
 *
 * get_ledger_status hides closed periods, so a sealed close was previously
 * unreachable. This view lists every period (closed first) with its sealed pack
 * manifest, and gives the real download surface the examiner-facing user needs:
 * a per-file Download on each artifact, and a "Download pack (.zip)" that bundles
 * the whole close. Strictly read-only — opening a past close here NEVER reopens
 * it (reopen stays a guarded correction on the close workbench). Reprints are
 * COPY-marked (r 38.6); these are the sealed originals, served verbatim.
 */
import { useState } from "react";
import { Link, useParams } from "react-router";
import { useLedgerPeriodHistory, type PeriodHistoryEntry } from "../../hooks/use-ledger-workbenches";
import { HashChip } from "../../components/ledger/HashChip";
import { mcpClient } from "../../../lib/mcp-client";

function FileIcon() { return <svg className="icon" viewBox="0 0 24 24"><path d="M6 2h9l5 5v15H6z" /></svg>; }

function PeriodCard({ ledgerId, p }: { ledgerId: string; p: PeriodHistoryEntry }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const closed = p.status === "closed";
  const hasPack = p.artifacts.length > 0;

  async function downloadPack() {
    setBusy(true); setErr(null);
    try { await mcpClient.downloadPeriodPack(ledgerId, p.period); }
    catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  }

  // Secure per-file download: bearer-fetch → blob → save (a plain window.open on
  // the get_file URL 401s on deployed workers — the bearer isn't a cookie).
  async function downloadArtifact(fileId: string) {
    setErr(null);
    try { await mcpClient.downloadFile(fileId); }
    catch (e) { setErr((e as Error).message); }
  }

  return (
    <div className="surface" style={{ borderRadius: "var(--border-radius-md)", marginBottom: 12 }}>
      <div className="surface__bar">
        <div className="surface__title"><strong>{p.period}</strong><span className="sub">{closed ? "closed" : p.status}</span></div>
        <span className="spacer" />
        {closed
          ? <span className="pill pill--consumed"><span className="dot" />closed{p.closed_at ? ` ${p.closed_at.slice(0, 10)}` : ""}</span>
          : <span className="pill pill--draft"><span className="dot" />{p.status}</span>}
        {hasPack ? <button data-variant="primary" style={{ marginLeft: 8 }} disabled={busy} onClick={downloadPack}>{busy ? "Bundling…" : "Download pack (.zip)"}</button> : null}
      </div>
      <div className="surface__section">
        {err ? <div className="stale-banner" style={{ marginBottom: 10 }}><svg className="icon" viewBox="0 0 24 24"><path d="M12 9v4m0 4h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /></svg><div>{err}</div></div> : null}
        {hasPack ? (
          p.artifacts.map((a) => (
            <div key={a.id} className="artifact-row">
              <FileIcon />
              <div><div>{a.kind.replace(/_/g, " ")}</div><span className="kind">{a.kind}{a.page_count ? ` · ${a.page_count}pp` : ""}</span></div>
              <HashChip hash={a.sha256} />
              <button style={{ padding: "2px 8px", fontSize: 12 }} onClick={() => downloadArtifact(a.file_id)}>Download</button>
            </div>
          ))
        ) : (
          <div className="muted tiny">{closed ? "Closed with no rendered artifacts." : "Open period — the pack is rendered when the period is closed."}</div>
        )}
      </div>
    </div>
  );
}

export function PeriodHistoryPage() {
  const { ledgerId } = useParams<{ ledgerId: string }>();
  const q = useLedgerPeriodHistory(ledgerId);

  return (
    <div className="screen">
      <div className="row" style={{ marginBottom: 10 }}><Link to={`/app/trust/${ledgerId}`} className="muted tiny" style={{ textDecoration: "none" }}>← Cockpit</Link></div>
      <div className="surface">
        <div className="surface__bar"><div className="surface__title"><strong>Period history</strong><span className="sub">closed periods & pack downloads</span></div></div>
        <div className="surface__section">
          {q.isLoading ? (
            <div className="checklist">{[0, 1, 2].map((i) => <div key={i} className="check-item"><span className="skeleton" style={{ width: 22, height: 22, borderRadius: "50%" }} /><div className="skeleton sk-line" style={{ width: "40%" }} /></div>)}</div>
          ) : q.isError || !q.data ? (
            <div className="error-state"><svg className="mark" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.4}><path d="M12 9v4m0 4h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /></svg><h3>Couldn’t load period history</h3><button data-variant="primary" onClick={() => q.refetch()}>Retry</button></div>
          ) : q.data.periods.length === 0 ? (
            <div className="empty-state"><h3>No periods yet</h3><p>Closed periods and their statutory packs will appear here.</p></div>
          ) : (
            q.data.periods.map((p) => <PeriodCard key={p.id} ledgerId={ledgerId!} p={p} />)
          )}
        </div>
      </div>
    </div>
  );
}
